import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import * as defaultVariables from 'src/scss/variables.module.scss';
import * as adminVariables from 'src/scss/admin/variables.module.scss';

import { lang, useLanguage } from './lang';
import moment from 'moment';
import LANG_CONST from './lang/const';
import { events } from './events';
import AlertManager from '../components/alert/AlertManager';

let locationRef = null;
export function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    let location = useLocation();
    let navigate = useNavigate();
    let params = useParams();
    let language = useLanguage();
    let screenDetail = useScreenDetail();

    useEffect(() => {
      let router = { location, navigate, params };
      if (locationRef !== JSON.stringify(location)) {
        locationRef = JSON.stringify(location);
        events.emit('changeRouter', router);
      }
    }, [location, navigate, params]);

    return (
      <Component
        {...props}
        router={{ location, navigate, params }}
        language={language}
        screenDetail={screenDetail}
      />
    );
  }

  return ComponentWithRouterProp;
}

var router = null;
export function setRouter(_router) {
  router = _router;
}
export function getRouter() {
  return router;
}

export function getImageSource() {
  return '';
}

function getDevice() {
  let userAgent = navigator.userAgent.toLowerCase();
  let platform = navigator.platform;
  if (userAgent.indexOf('android') > -1) return 'android';
  if (['iPhone', 'iPhone Simulator', 'iPod', 'iPod Simulator'].includes(platform)) return 'ios';
  return 'other';
}
export const device = getDevice();

export const deviceIsMobile = !!(
  /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(
    navigator.userAgent
  ) ||
  // eslint-disable-next-line no-useless-escape -- preserve upstream mobile UA fallback regex
  /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
    navigator.userAgent.slice(0, 4)
  )
);

export const uniqueArray = (arr, key = null) => {
  const list = {};
  arr.forEach((item) => {
    if (key) {
      list[item[key]] = item;
    } else {
      list[item] = item;
    }
  });
  return Object.values(list);
};

export const hasSameId = (left, right) => {
  return (
    left !== null &&
    left !== undefined &&
    right !== null &&
    right !== undefined &&
    String(left) === String(right)
  );
};

export function getAge(dateString, hasUnit = false) {
  if (!dateString) {
    return null;
  }
  const today = new Date();
  const birthDate = new Date(
    dateString.toString().length === 8 ? moment(dateString, 'YYYYMMDD').valueOf() : dateString
  );
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  if (hasUnit) {
    return lang.t(age > 1 ? LANG_CONST.amount_years : LANG_CONST.amount_year, { amount: age });
  }
  return age;
}

export function objectToSelectList(obj, trans = true, sort = false) {
  let list = Object.keys(obj).map((key) => ({
    value: key,
    label: trans ? lang.t(obj[key]) : obj[key]
  }));

  if (sort) {
    list = list.sort((a, b) => (a.value > b.value ? 1 : -1));
  }

  return list;
}

export function rangeToSelectList(from, to, reverse = false, padLen = null) {
  let list = [];
  for (let i = from; i <= to; i++) {
    list.push({ value: i, label: padLen ? String(i).padStart(padLen, '0') : i });
  }

  return reverse ? list.reverse() : list;
}

export const upperFirstChar = (text) => {
  return text && text.charAt(0).toLocaleUpperCase() + text.slice(1);
};

export const upperFirstCharAll = (text) => {
  return (
    text &&
    text
      .split(' ')
      .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
      .join(' ')
  );
};

export const countFormat = (num) => {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'G';
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num || 0;
};

export const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) {
    return null;
  }
  const total = Math.max(0, Math.floor(seconds));
  const mm = String(Math.floor(total / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

export const number = new Intl.NumberFormat('en-US');

export const userFullName = (user, isDoctor = false, def = '') =>
  !user?.firstName
    ? def
    : (isDoctor ? 'Dr. ' : '') + (user?.firstName + ' ' + (user?.lastName || ''));

export const isAdminPage = () => window.location.href.indexOf('/admin') > -1;

const variables = isAdminPage() ? adminVariables : defaultVariables;
export const scssVariables = variables.default ? variables.default : variables;

export const remCss = (w) => {
  if (!isNaN(parseFloat(w)) && isFinite(w)) {
    return w * scssVariables['mc-resize'];
  }

  return w;
};

export const remFontCss = (w) => {
  if (!isNaN(parseFloat(w)) && isFinite(w)) {
    return w * scssVariables['mc-font-resize'];
  }

  return w;
};

export const getSelectLabel = (options, values, joinChar = ', ') => {
  if (Array.isArray(values)) {
    const list = options.filter((i) => values.indexOf(i.value) > -1).map((i) => lang.t(i.label));
    return typeof joinChar === 'string'
      ? list.join(joinChar)
      : list.map((o) => (
          <React.Fragment key={o}>
            {o}
            {joinChar}
          </React.Fragment>
        ));
  }
  return lang.t(options.find((i) => i.value === values)?.label || '');
};

export const rgba = (hex, o = 1) => {
  if (hex === '#ffff00') hex = '#ff0';
  let result;
  if (hex.length < 7) {
    result = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(hex);
    result[1] = `${result[1]}${result[1]}`;
    result[2] = `${result[2]}${result[2]}`;
    result[3] = `${result[3]}${result[3]}`;
  } else {
    result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  }

  const { r, g, b } = result
    ? {
        r: parseInt(result[1] || 0, 16),
        g: parseInt(result[2] || 0, 16),
        b: parseInt(result[3] || 0, 16)
      }
    : null;
  return `rgba(${r}, ${g}, ${b}, ${o})`;
};

export const dateOfWeek = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];

export const humanFileSize = (bytes, si = false, dp = 1) => {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return bytes.toFixed(dp) + ' ' + units[u];
};

export const addressObject = ({ countryAddress = '', stateAddress = '', cityAddress = '' }) => {
  const countryAddressPieces = (countryAddress || '').split('_');
  const stateAddressPieces = (stateAddress || '').split('_');
  const cityAddressPieces = (cityAddress || '').split('_');

  const countryCode = countryAddressPieces[1] || cityAddressPieces[2] || '';
  const countryName = countryAddressPieces[0] || countryCode;

  return {
    countryName,
    countryCode,
    stateName: stateAddressPieces[0] || '',
    stateCode: stateAddressPieces[1] || cityAddressPieces[1] || '',
    cityName: cityAddressPieces[0] || '',
    fullAddressName: [countryName, stateAddressPieces[0], cityAddressPieces[0]]
      .filter((v) => v)
      .join(', '),
    fullAddressNameReverse: [cityAddressPieces[0], stateAddressPieces[0], countryName]
      .filter((v) => v)
      .join(', '),
    shortAddressNameReverse: [cityAddressPieces[0], stateAddressPieces[0], countryCode]
      .filter((v) => v)
      .join(', ')
  };
};

export function useScreenDetail() {
  const [isSmallScreen, setIsSmallScreen] = useState(
    window.innerWidth < scssVariables['width-to-mobile']
  );
  const [isMediumScreen, setIsMediumScreen] = useState(
    !isSmallScreen && window.innerWidth < scssVariables['width-to-desktop']
  );
  const [isLargeScreen, setIsLargeScreen] = useState(!isSmallScreen && !isMediumScreen);
  const [innerWidth, setInnerWidth] = useState(window.innerWidth);

  const resize = useCallback(() => {
    let isSmall = window.innerWidth < scssVariables['width-to-mobile'];
    let isMedium = !isSmall && window.innerWidth < scssVariables['width-to-desktop'];
    let isLarge = !isSmall && !isMedium;

    setIsSmallScreen(isSmall);
    setIsMediumScreen(isMedium);
    setIsLargeScreen(isLarge);
    setInnerWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  return {
    isSmallScreen,
    isMediumScreen,
    isLargeScreen,
    innerWidth,
    innerHeight: window.innerHeight
  };
}

export const getFileInfoFromUri = (file, defaultType = 'application/octet-stream') => {
  if (typeof file !== 'string') return file;

  let extension = file.split('.').pop().toLocaleLowerCase();

  let types = {
    aac: 'audio/aac',
    abw: 'application/x-abiword',
    arc: 'application/x-freearc',
    avi: 'video/x-msvideo',
    azw: 'application/vnd.amazon.ebook',
    bin: 'application/octet-stream',
    bmp: 'image/bmp',
    bz: 'application/x-bzip',
    bz2: 'application/x-bzip2',
    cda: 'application/x-cdf',
    csh: 'application/x-csh',
    css: 'text/css',
    csv: 'text/csv',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    eot: 'application/vnd.ms-fontobject',
    epub: 'application/epub+zip',
    gz: 'application/gzip',
    gif: 'image/gif',
    htm: 'text/html',
    html: 'text/html',
    ico: 'image/vnd.microsoft.icon',
    ics: 'text/calendar',
    jar: 'application/java-archive',
    jpeg: 'image/jpeg',
    jfif: 'image/jpeg',
    jif: 'image/jpeg',
    jpg: 'image/jpeg',
    js: 'text/javascript',
    json: 'application/json',
    jsonld: 'application/ld+json',
    mid: 'audio/midi audio/x-midi',
    midi: 'audio/midi audio/x-midi',
    mjs: 'text/javascript',
    mp3: 'audio/mpeg',
    mp4: 'video/mp4',
    mpeg: 'video/mpeg',
    mpkg: 'application/vnd.apple.installer+xml',
    odp: 'application/vnd.oasis.opendocument.presentation',
    ods: 'application/vnd.oasis.opendocument.spreadsheet',
    odt: 'application/vnd.oasis.opendocument.text',
    oga: 'audio/ogg',
    ogv: 'video/ogg',
    ogx: 'application/ogg',
    opus: 'audio/opus',
    otf: 'font/otf',
    png: 'image/png',
    pdf: 'application/pdf',
    php: 'application/x-httpd-php',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    rar: 'application/vnd.rar',
    rtf: 'application/rtf',
    sh: 'application/x-sh',
    svg: 'image/svg+xml',
    swf: 'application/x-shockwave-flash',
    tar: 'application/x-tar',
    tif: 'image/tiff',
    tiff: 'image/tiff',
    ts: 'video/mp2t',
    ttf: 'font/ttf',
    txt: 'text/plain',
    vsd: 'application/vnd.visio',
    wav: 'audio/wav',
    weba: 'audio/webm',
    webm: 'video/webm',
    webp: 'image/webp',
    woff: 'font/woff',
    woff2: 'font/woff2',
    xhtml: 'application/xhtml+xml',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xml: 'application/xml',
    xul: 'application/vnd.mozilla.xul+xml',
    zip: 'application/zip',
    '3gp': 'video/3gpp',
    '3g2': 'video/3gpp2',
    '7z': 'application/x-7z-compressed'
  };

  let type = types[extension] || defaultType;
  return { extension, type, mimetype: type, uri: file };
};

export const formatDate = (date) =>
  moment(date).calendar(null, {
    sameDay: '[Today at] HH:mm',
    nextDay: '[Tomorrow at] HH:mm',
    nextWeek: 'ddd [at] HH:mm',
    lastDay: '[Yesterday at] HH:mm',
    lastWeek: '[Last] ddd [at] HH:mm',
    sameElse: function (now) {
      if (this.isSame(now, 'year')) {
        return 'MMM DD [at] HH:mm';
      }

      return 'DD/MM/YYYY [at] HH:mm';
    }
  });

export const convertMobileStyle = (style = {}) => {
  if (!style) style = {};
  let { paddingHorizontal, paddingVertical, marginHorizontal, marginVertical, ...rest } = style;

  if (paddingHorizontal) {
    rest.paddingLeft = paddingHorizontal;
    rest.paddingRight = paddingHorizontal;
  }
  if (paddingVertical) {
    rest.paddingTop = paddingVertical;
    rest.paddingBottom = paddingVertical;
  }
  if (marginHorizontal) {
    rest.marginLeft = marginHorizontal;
    rest.marginRight = marginHorizontal;
  }
  if (marginVertical) {
    rest.marginTop = marginVertical;
    rest.marginBottom = marginVertical;
  }

  return rest;
};

export const openApp = (data) => {
  if (device === 'ios' || device === 'android') {
    events.emit('openApp', data);
    return true;
  } else {
    return false;
  }
};

export const checkAvatarDimension = (file, width = 640, height = 640) => {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = function () {
      if (this.width < width || this.height < height) {
        AlertManager.popup(AlertManager.TYPES.WARN, {
          close: false,
          title: lang.t(LANG_CONST.validation.image_resolution, {
            resolution: `${width}x${height}`
          }),
          actionButton: {
            label: LANG_CONST.buttons.ok,
            action: (actions) => actions.hide()
          }
        });
        resolve(false);
      } else {
        resolve(true);
      }
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
};

export const getFileType = (file) => {
  let mineType = file.mimetype || file.type;
  let type = mineType ? mineType.toString().split('/')[0] : null;
  type = type === 'application' && mineType.includes('/pdf') ? 'pdf' : type;

  return type;
};

export const getThumbnailFromVideoFile = (file) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const video = document.createElement('video');

    // this is important
    video.autoplay = true;
    video.muted = true;
    let url = URL.createObjectURL(file);
    video.src = url;

    video.onloadeddata = () => {
      let ctx = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
      video.pause();
      URL.revokeObjectURL(url);
      return resolve(canvas.toDataURL('image/png'));
    };

    video.onerror = (e) => {
      URL.revokeObjectURL(url);
      console.error('Error loading video:', e);
      return resolve(null);
    };
  });
};

export const formatDateRange = (startDate, endDate, opts = {}) => {
  const options = {
    separate: ' - ',
    format: 'D.MM.YYYY',
    startDateIfSameMonth: 'D',
    startDateIfSameYear: 'D.MM',
    currentYearFormat: 'D.MM.YYYY',
    ...opts
  };

  const start = moment(startDate);
  const now = moment();
  const currentYear = start.year() === now.year();

  if (!endDate || start.isSame(endDate, 'date')) {
    if (currentYear) {
      return start.format(options.currentYearFormat);
    } else {
      return start.format(options.format);
    }
  }
  const end = moment(endDate);

  const sameYear = start.year() === end.year();
  const sameMonth = start.month() === end.month() && sameYear;

  if (sameMonth) {
    if (currentYear) {
      return `${start.format(options.startDateIfSameMonth)}${options.separate}${end.format(options.currentYearFormat)}`;
    } else {
      return `${start.format(options.startDateIfSameMonth)}${options.separate}${end.format(options.format)}`;
    }
  } else if (sameYear) {
    if (currentYear) {
      return `${start.format(options.startDateIfSameYear)}${options.separate}${end.format(options.currentYearFormat)}`;
    } else {
      return `${start.format(options.startDateIfSameYear)}${options.separate}${end.format(options.format)}`;
    }
  } else {
    return `${start.format(options.format)}${options.separate}${end.format(options.format)}`;
  }
};

export const getImageHash = async function (file) {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

export const getRemoteImageHash = async function (remoteUrl) {
  // https:// is allowed by CSP, only blob: is blocked
  const blob = await fetch(remoteUrl).then((r) => r.blob());
  return getImageHash(blob);
};

export const checkSameAsAvatar = async function (croppedBlob, currentAvatarUrls = []) {
  if (currentAvatarUrls.length === 0) return null;
  const [croppedHash, ...avatarHashes] = await Promise.all([
    getImageHash(croppedBlob),
    ...currentAvatarUrls.map((current) => getRemoteImageHash(current.replace('full_', 'orig_')))
  ]);

  return avatarHashes.includes(croppedHash) ? LANG_CONST.validation.avatar_used : null;
};

// WARNING: decode only, the signature is NOT verified here.
// Use the result for UI hints only (name, avatar). Never trust it for auth.
// The backend must verify the token (signature, aud, iss, exp).
export const generateGoogleIdToken = (gid_token) => {
  try {
    const base64 = gid_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
          .join('')
      )
    );
    if (payload) {
      // Remove accents. Missing fields become '' (family_name can be absent).
      const clean = (v) =>
        typeof v === 'string' ? v.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '';
      payload.name = clean(payload.name);
      payload.firstName = clean(payload.given_name);
      payload.lastName = clean(payload.family_name);
      return payload;
    }
  } catch {
    return null;
  }

  return null;
};

export const isReactText = (children) => {
  return ['string', 'number'].includes(typeof children);
};
